import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ophelos/ui/components/accordion';
import { ArrowDownRight, ArrowUpRight, ArrowDownToLine, FileText } from 'lucide-react';
import { useMemo } from 'react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { TransactionItem } from '@/types/core-api';
import { ADJUSTMENT_TYPES, type ChargeRow, chargesSubject, type InvoiceGroup, listChargesGrouped, parseChargeDate } from './charges';

interface ChargesBreakdownProps {
  transactions: TransactionItem[];
  currency?: string;
}

function SignedAmount({ amount, currency, className = '' }: { amount: number; currency?: string; className?: string }) {
  const isCredit = amount < 0;
  return (
    <span className={`${className} ${isCredit ? 'text-green-600' : ''}`}>
      {isCredit ? '- ' : ''}
      <CurrencyDisplay amount={Math.abs(amount)} currency={currency} />
    </span>
  );
}

/**
 * The original debt's components read as amber down-arrows — they are what was owed from the
 * start, not a movement. Adjustments read as the direction they moved the balance: amber up for
 * an increase, green down for a reduction.
 */
function ChargeBadge({ row }: { row: ChargeRow }) {
  const isOriginal = !ADJUSTMENT_TYPES.has(row.type);
  const isDecrease = !isOriginal && row.amount < 0;

  return (
    <div
      className={`size-7 rounded-full flex items-center justify-center shrink-0 ${isOriginal ? 'bg-muted' : isDecrease ? 'highlight-green' : 'highlight-amber'}`}
    >
      {isOriginal ? (
        <ArrowDownToLine className="size-3.5" />
      ) : isDecrease ? (
        <ArrowDownRight className="size-3.5" />
      ) : (
        <ArrowUpRight className="size-3.5" />
      )}
    </div>
  );
}

/** One charge on the balance. */
function ChargeLine({
  row,
  currency,
  formatDate,
  typeLabel,
}: {
  row: ChargeRow;
  currency?: string;
  formatDate: (d: string) => string;
  typeLabel: (t: string) => string;
}) {
  return (
    <div className="py-3 border-b border-border-light last:border-b-0">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <ChargeBadge row={row} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{row.description}</p>
            <p className="text-xs text-muted-foreground">
              {row.date ? formatDate(row.date) : '—'} · {typeLabel(row.type)}
            </p>
          </div>
        </div>
        <SignedAmount amount={row.amount} currency={currency} className="text-sm font-semibold whitespace-nowrap" />
      </div>
    </div>
  );
}

/**
 * The charges billed together on one invoice.
 *
 * The components stay visible at all times. A collapsed group would hide charges the customer is
 * owed sight of, so grouping here is typography — the outer accordion is the only thing that folds.
 *
 * The header is deliberately positional (`Invoice 1 of 3`) rather than named: every invoice on a
 * case arrives with the same description and the same load date, so neither distinguishes one
 * invoice from another. The reference embeds a billing date, but roughly half the book has no
 * parseable one, so titling by date would leave most invoices blank. The shared description is
 * a case-level fact and is stated once above the list — see {@link chargesSubject}.
 */
function InvoiceGroupBlock({
  group,
  position,
  total,
  currency,
  typeLabel,
}: {
  group: InvoiceGroup;
  position: number;
  total: number;
  currency?: string;
  typeLabel: (t: string) => string;
}) {
  const { t } = useTranslation();

  return (
    <div className="py-3 border-b border-border-light last:border-b-0" data-testid="invoice-group">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="size-7 rounded-full flex items-center justify-center shrink-0 bg-muted">
            <FileText className="size-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{t('debtBreakdown.invoiceOrdinal', { n: position, m: total })}</p>
            <p className="text-xs text-muted-foreground">{t('debtBreakdown.chargeCount', { n: group.charges.length })}</p>
          </div>
        </div>
        <SignedAmount amount={group.amount} currency={currency} className="text-sm font-semibold whitespace-nowrap" />
      </div>
      <div className="mt-2 pl-10 stack gap-1">
        {group.charges.map((charge) => (
          <div key={charge.rowId} className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground truncate">{typeLabel(charge.type)}</span>
            <SignedAmount amount={charge.amount} currency={currency} className="text-xs text-muted-foreground whitespace-nowrap" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Every charge behind the balance — the original billed components and any adjustments, newest
 * first. Charges a client billed on one invoice are grouped under it; everything else stands alone.
 */
export function ChargesBreakdown({ transactions, currency }: ChargesBreakdownProps) {
  const { t, currentLanguage } = useTranslation();

  const items = useMemo(() => listChargesGrouped(transactions), [transactions]);
  const invoiceCount = items.filter((item) => item.kind === 'invoice').length;
  // A flat list already names each charge on its own row; only grouped invoices lose the subject.
  const subject = invoiceCount > 0 ? chargesSubject(transactions) : null;

  if (items.length === 0) return null;

  const formatDate = (dateStr: string) => {
    const parsed = parseChargeDate(dateStr);
    if (!parsed) return dateStr;
    return parsed.toLocaleDateString(currentLanguage, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // `fee` is the ledger's singular type; the customer-facing string is the shared `fees` label.
  const typeLabel = (type: string) => t(type === 'fee' ? 'transaction.fees' : `transaction.${type}`);

  return (
    <Accordion type="single" collapsible data-testid="charges-breakdown">
      <AccordionItem value="charges" className="border-b-0">
        <AccordionTrigger className="py-1 justify-center gap-2 text-sm text-muted-foreground font-normal hover:bg-transparent">
          {t('debtBreakdown.viewHistory')}
        </AccordionTrigger>
        <AccordionContent className="pt-2">
          {subject && (
            <p className="text-sm font-medium pb-2 border-b border-border-light" data-testid="charges-subject">
              {subject}
            </p>
          )}
          {items.map((item, index) =>
            item.kind === 'invoice' ? (
              <InvoiceGroupBlock
                key={item.groupId}
                group={item}
                position={items.slice(0, index).filter((i) => i.kind === 'invoice').length + 1}
                total={invoiceCount}
                currency={currency}
                typeLabel={typeLabel}
              />
            ) : (
              <ChargeLine key={item.charge.rowId} row={item.charge} currency={currency} formatDate={formatDate} typeLabel={typeLabel} />
            ),
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
